// Action buttons for the detail page header. Lives in the right side of the
// PageHeader (campaign name lives on the left). Status strip + meta are
// rendered separately by CampaignStatusStrip below the page header.

import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SimpleButton } from '../../../../ui';
import ConfirmDelete from '../../../../ui/Modal/ConfirmDelete';
import { ACTION, getActions } from '../../utils/campaignActions';
import { useCampaignPermissions } from '../../hooks/useCampaignPermissions';
import { useCampaignsActions } from '../../hooks/useCampaignsActions';
import { CAMPAIGN_STATUS } from '../../constants';

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
`;

const CampaignHeader = ({ campaign }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useCampaignPermissions();
  const { updateStatus, duplicateCampaign, deleteCampaign, restoreCampaign } = useCampaignsActions();

  const [pendingDelete, setPendingDelete] = useState(false);

  const actions = getActions(campaign, permissions);

  const renderAction = useCallback(
    (action) => {
      switch (action) {
        case ACTION.EDIT:
          return (
            <SimpleButton key={action} variant='tertiary' startIcon='Edit' onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}>
              {t('globals.edit', 'Edit')}
            </SimpleButton>
          );
        case ACTION.CANCEL:
          return (
            <SimpleButton key={action} variant='tertiary' onClick={() => updateStatus({ id: campaign.id, status: CAMPAIGN_STATUS.DRAFT })}>
              {t('campaigns.cancelSchedule', 'Cancel schedule')}
            </SimpleButton>
          );
        case ACTION.PAUSE:
          return (
            <SimpleButton key={action} variant='tertiary' startIcon='Pause' onClick={() => updateStatus({ id: campaign.id, status: CAMPAIGN_STATUS.PAUSED })}>
              {t('campaigns.pause', 'Pause')}
            </SimpleButton>
          );
        case ACTION.RESUME:
          return (
            <SimpleButton key={action} variant='primary' startIcon='Play' onClick={() => updateStatus({ id: campaign.id, status: CAMPAIGN_STATUS.ACTIVE })}>
              {t('campaigns.resume', 'Resume')}
            </SimpleButton>
          );
        case ACTION.DUPLICATE:
          return (
            <SimpleButton key={action} variant='tertiary' startIcon='Copy' onClick={() => duplicateCampaign(campaign.id)}>
              {t('globals.duplicate', 'Duplicate')}
            </SimpleButton>
          );
        case ACTION.DELETE:
          return (
            <SimpleButton key={action} variant='tertiary' startIcon='Delete' onClick={() => setPendingDelete(true)}>
              {t('globals.delete', 'Delete')}
            </SimpleButton>
          );
        case ACTION.RESTORE:
          return (
            <SimpleButton key={action} variant='primary' onClick={() => restoreCampaign(campaign.id)}>
              {t('campaigns.restore', 'Restore')}
            </SimpleButton>
          );
        default:
          return null;
      }
    },
    [campaign.id, navigate, t, updateStatus, duplicateCampaign, restoreCampaign],
  );

  return (
    <>
      <Actions>{actions.map(renderAction)}</Actions>
      <ConfirmDelete
        open={pendingDelete}
        title={t('campaigns.deleteTitle', 'Delete campaign')}
        description={t('campaigns.confirmDelete', { name: campaign.name, defaultValue: "Delete '{{name}}'? This can't be undone." })}
        onAcceptDelete={async () => {
          await deleteCampaign(campaign.id);
          setPendingDelete(false);
          navigate('/campaigns');
        }}
        onClose={() => setPendingDelete(false)}
      />
    </>
  );
};

export default CampaignHeader;
